import { LitElement, html, css, svg } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import '@material/web/select/outlined-select.js'
import '@material/web/select/select-option.js'
import '../common/grouped-select.js'
import type { GroupedSelectGroup, GroupedSelectItem } from '../common/grouped-select.js'

export type MarkerType = 'none' | 'circle' | 'square' | 'triangle' | 'pointer'
export type MarkerPosition = 'center' | 'above' | 'below'

const ICON_UNGROUPED: GroupedSelectItem[] = [{ value: '', label: 'None' }]

const ICON_GROUPS: GroupedSelectGroup[] = [
  {
    label: 'Navigation',
    items: [
      { value: 'location_on', label: 'Location', icon: 'location_on' },
      { value: 'flag', label: 'Flag', icon: 'flag' },
      { value: 'explore', label: 'Compass', icon: 'explore' },
      { value: 'near_me', label: 'Waypoint', icon: 'near_me' },
      { value: 'signpost', label: 'Signpost', icon: 'signpost' },
    ],
  },
  {
    label: 'Camping & Shelter',
    items: [
      { value: 'camping', label: 'Tent', icon: 'camping' },
      { value: 'cabin', label: 'Cabin', icon: 'cabin' },
      { value: 'cottage', label: 'Shelter', icon: 'cottage' },
      { value: 'local_fire_department', label: 'Campfire', icon: 'local_fire_department' },
    ],
  },
  {
    label: 'Water',
    items: [
      { value: 'water_drop', label: 'Water Source', icon: 'water_drop' },
      { value: 'waves', label: 'River/Lake', icon: 'waves' },
      { value: 'pool', label: 'Swimming', icon: 'pool' },
    ],
  },
  {
    label: 'Hazards & Safety',
    items: [
      { value: 'warning', label: 'Hazard', icon: 'warning' },
      { value: 'emergency', label: 'Emergency', icon: 'emergency' },
      { value: 'local_hospital', label: 'First Aid', icon: 'local_hospital' },
      { value: 'do_not_disturb_on', label: 'Restricted', icon: 'do_not_disturb_on' },
    ],
  },
  {
    label: 'Nature & Terrain',
    items: [
      { value: 'terrain', label: 'Peak/Summit', icon: 'terrain' },
      { value: 'landscape', label: 'Viewpoint', icon: 'landscape' },
      { value: 'forest', label: 'Forest', icon: 'forest' },
      { value: 'pets', label: 'Wildlife', icon: 'pets' },
    ],
  },
  {
    label: 'Infrastructure',
    items: [
      { value: 'local_parking', label: 'Trailhead/Parking', icon: 'local_parking' },
      { value: 'directions', label: 'Junction', icon: 'directions' },
      { value: 'gate', label: 'Gate', icon: 'gate' },
    ],
  },
  {
    label: 'Activity',
    items: [
      { value: 'hiking', label: 'Hiking', icon: 'hiking' },
      { value: 'downhill_skiing', label: 'Skiing', icon: 'downhill_skiing' },
      { value: 'kayaking', label: 'Paddling', icon: 'kayaking' },
      { value: 'photo_camera', label: 'Photo Spot', icon: 'photo_camera' },
    ],
  },
  {
    label: 'Points of Interest',
    items: [
      { value: 'star', label: 'Favorite', icon: 'star' },
      { value: 'info', label: 'Info', icon: 'info' },
      { value: 'restaurant', label: 'Food', icon: 'restaurant' },
      { value: 'home', label: 'Home/Base', icon: 'home' },
    ],
  },
]

const MARKER_TYPES: { type: MarkerType; label: string }[] = [
  { type: 'none', label: 'None' },
  { type: 'circle', label: 'Circle' },
  { type: 'square', label: 'Square' },
  { type: 'triangle', label: 'Triangle' },
  { type: 'pointer', label: 'Pin' },
]

export type MarkerSize = 'xs' | 's' | 'm' | 'l' | 'xl' | 'xxl'

const MARKER_SIZES: { size: MarkerSize; label: string; px: number }[] = [
  { size: 'xs', label: 'Extra Small', px: 12 },
  { size: 's', label: 'Small', px: 18 },
  { size: 'm', label: 'Medium', px: 24 },
  { size: 'l', label: 'Large', px: 32 },
  { size: 'xl', label: 'Extra Large', px: 42 },
  { size: 'xxl', label: 'Huge', px: 64 },
]

export function markerSizeToPx(size: MarkerSize | string): number {
  const entry = MARKER_SIZES.find(s => s.size === size)
  return entry ? entry.px : 12
}

export function pxToMarkerSize(px: number): MarkerSize {
  let closest: MarkerSize = 'xs'
  let minDiff = Infinity
  for (const s of MARKER_SIZES) {
    const diff = Math.abs(s.px - px)
    if (diff < minDiff) {
      minDiff = diff
      closest = s.size
    }
  }
  return closest
}

const MARKER_POSITIONS: { position: MarkerPosition; label: string }[] = [
  { position: 'center', label: 'Centered' },
  { position: 'above', label: 'Above' },
  { position: 'below', label: 'Below' },
]

@customElement('marker-editor-dropdown')
export class MarkerEditorDropdown extends LitElement {
  static override styles = css`
    :host {
      position: absolute;
      z-index: 2000;
    }

    .dropdown {
      background: rgba(30, 41, 59, 0.95);
      backdrop-filter: blur(12px);
      -webkit-backdrop-filter: blur(12px);
      border-radius: 12px;
      padding: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
      width: 232px;
      display: flex;
      flex-direction: column;
      gap: 10px;
      max-height: 70vh;
      overflow-y: visible;
    }

    .section-label {
      color: rgba(255, 255, 255, 0.6);
      font-size: 11px;
      font-weight: 500;
    }

    .divider {
      height: 1px;
      background: rgba(255, 255, 255, 0.12);
      margin: 2px 0;
    }

    md-outlined-select {
      width: 100%;
      --md-outlined-select-text-field-container-height: 40px;
      --md-outlined-select-text-field-container-shape: 8px;
      --md-outlined-field-outline-color: rgba(255, 255, 255, 0.3);
      --md-outlined-field-hover-outline-color: rgba(255, 255, 255, 0.5);
      --md-outlined-field-focus-outline-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-input-text-color: white;
      --md-outlined-select-text-field-label-text-color: rgba(255, 255, 255, 0.6);
      --md-outlined-select-text-field-trailing-icon-color: rgba(255, 255, 255, 0.7);
      --md-outlined-select-text-field-input-text-size: 13px;
      --md-sys-color-surface-container: rgba(30, 41, 59, 0.98);
      --md-sys-color-on-surface: white;
      --md-sys-color-on-surface-variant: rgba(255, 255, 255, 0.7);
      --md-menu-container-color: rgba(30, 41, 59, 0.98);
      --md-menu-container-shape: 8px;
    }

    .option-content {
      display: flex;
      align-items: center;
      gap: 10px;
    }

    .option-icon {
      font-family: 'Material Symbols Outlined';
      font-size: 18px;
      width: 24px;
      text-align: center;
    }

    .marker-preview {
      flex-shrink: 0;
    }
  `

  @property({ type: String })
  markerType: MarkerType = 'circle'

  @property({ type: String })
  markerIcon = ''

  @property({ type: String })
  markerSize: MarkerSize = 'xs'

  @property({ type: String })
  markerPosition: MarkerPosition = 'center'

  private handleTypeChange(e: Event): void {
    const select = e.target as HTMLSelectElement
    if (select.value) {
      this.markerType = select.value as MarkerType
      this.markerPosition =
        this.markerType === 'pointer'
          ? 'above'
          : this.markerType === 'triangle'
            ? 'below'
            : 'center'
      this.dispatchChange()
    }
  }

  private handleIconChange(e: CustomEvent): void {
    this.markerIcon = e.detail.value
    if (
      this.markerIcon &&
      MARKER_SIZES.findIndex(s => s.size === this.markerSize) <
        MARKER_SIZES.findIndex(s => s.size === 'm')
    ) {
      this.markerSize = 'm'
    }
    this.dispatchChange()
  }

  private handleSizeChange(e: Event): void {
    const select = e.target as HTMLSelectElement
    if (select.value) {
      this.markerSize = select.value as MarkerSize
      this.dispatchChange()
    }
  }

  private handlePositionChange(e: Event): void {
    const select = e.target as HTMLSelectElement
    if (select.value) {
      this.markerPosition = select.value as MarkerPosition
      this.dispatchChange()
    }
  }

  private dispatchChange(): void {
    this.dispatchEvent(
      new CustomEvent('marker-changed', {
        detail: {
          markerType: this.markerType,
          markerIcon: this.markerIcon,
          markerSize: markerSizeToPx(this.markerSize),
          markerPosition: this.markerPosition,
        },
        bubbles: true,
      })
    )
  }

  private renderMarkerTypePreview(type: MarkerType): unknown {
    const s = 24
    switch (type) {
      case 'none':
        return svg`<svg class="marker-preview" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}"><line x1="4" y1="4" x2="20" y2="20" stroke="currentColor" stroke-width="1.5" opacity="0.4"/><line x1="20" y1="4" x2="4" y2="20" stroke="currentColor" stroke-width="1.5" opacity="0.4"/></svg>`
      case 'circle':
        return svg`<svg class="marker-preview" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}"><circle cx="12" cy="12" r="8" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="1.5"/></svg>`
      case 'square':
        return svg`<svg class="marker-preview" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}"><rect x="4" y="4" width="16" height="16" rx="2" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="1.5"/></svg>`
      case 'triangle':
        return svg`<svg class="marker-preview" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}"><polygon points="12,3 22,21 2,21" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="1.5" stroke-linejoin="round"/></svg>`
      case 'pointer':
        return svg`<svg class="marker-preview" width="${s}" height="${s}" viewBox="0 0 ${s} ${s}"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="currentColor" opacity="0.3" stroke="currentColor" stroke-width="1.5"/></svg>`
    }
  }

  override render(): unknown {
    return html`
      <div class="dropdown">
        <md-outlined-select
          label="Marker"
          .value=${this.markerType}
          @change=${this.handleTypeChange}
        >
          ${MARKER_TYPES.map(
            m => html`
              <md-select-option value=${m.type} ?selected=${this.markerType === m.type}>
                <div slot="headline" class="option-content">
                  ${this.renderMarkerTypePreview(m.type)}
                  <span>${m.label}</span>
                </div>
              </md-select-option>
            `
          )}
        </md-outlined-select>

        <grouped-select
          label="Icon"
          .value=${this.markerIcon}
          .ungroupedItems=${ICON_UNGROUPED}
          .groups=${ICON_GROUPS}
          @value-changed=${this.handleIconChange}
        ></grouped-select>

        <md-outlined-select
          label="Position"
          .value=${this.markerPosition}
          @change=${this.handlePositionChange}
        >
          ${MARKER_POSITIONS.map(
            p => html`
              <md-select-option value=${p.position} ?selected=${this.markerPosition === p.position}>
                <div slot="headline">${p.label}</div>
              </md-select-option>
            `
          )}
        </md-outlined-select>

        <md-outlined-select label="Size" .value=${this.markerSize} @change=${this.handleSizeChange}>
          ${MARKER_SIZES.filter(
            s =>
              !this.markerIcon ||
              MARKER_SIZES.indexOf(s) >= MARKER_SIZES.findIndex(e => e.size === 'm')
          ).map(
            s => html`
              <md-select-option value=${s.size} ?selected=${this.markerSize === s.size}>
                <div slot="headline">${s.label}</div>
              </md-select-option>
            `
          )}
        </md-outlined-select>
      </div>
    `
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'marker-editor-dropdown': MarkerEditorDropdown
  }
}
